/**
 * The idea chips the wizard showed before they moved to the admin (they lived
 * in StoryInputStep.tsx). Seeds the story_ideas table (migration 0110, checked
 * by a test) and stays the wizard's fallback while the table is empty or
 * unavailable. Edit ideas in the admin, not here.
 */
/** Localized idea seeds to fight blank-page fear. {name} is replaced with the
 *  picked child's name (or a neutral hero word). Kept in-code (not i18n arrays)
 *  for simplicity — these are UX sugar, not product copy. */
export const SEED_STORY_IDEAS: Record<string, string[]> = {
  ru: [
    "{name} находит в саду говорящего дракончика",
    "{name} спасает Новый год",
    "Как {name} подружил(а) луну и солнце",
    "{name} и потерянная звезда",
    "Большое приключение {name} под водой",
    "{name} и радуга, которая убежала",
    "{name} строит ракету из картона",
    "{name} встречает облачко, которое любит чихать",
    "{name} и щекотливый великан",
    "{name} открывает магазин для одиноких игрушек",
    "{name} и зонтик, который летает",
    "Как {name} научил(а) улитку бегать наперегонки",
    "{name} и дверца в старом дубе",
    "{name} печёт пирог для всего леса",
    "{name} и застенчивое привидение",
    "{name} спасает русалку из лужи",
    "{name} и гоблин, ворующий носки",
    "{name} раскрашивает серый город",
    "{name} и книжка из библиотеки, которая шепчет",
    "{name} устраивает праздник для дождинок",
    "{name} и гора, что напевает колыбельные",
    "{name} дружит с роботом, который разучился смеяться",
    "{name} и крошечный цирк под кроватью",
    "{name} идёт по карте из звёзд",
    "{name} и зима, которая не хотела уходить",
    "{name} учит пингвинов запускать воздушного змея",
    "{name} и шепчущая ракушка",
    "{name} и похититель мёда на лугу",
    "{name} находит город внутри музыкальной шкатулки",
    "{name} и шарик, исполняющий желания",
    "{name} помогает осенним листьям вернуться домой",
    "{name} и щенок из облаков",
    "{name} и тайная дверца в холодильнике",
    "{name} устраивает день рождения столетней черепахе",
    "{name} и сонный вулкан",
    "{name} и письмо из будущего",
    "{name} и сад, который растёт по ночам",
    "{name} дружит с последним светлячком лета",
    "{name} и сбежавшая тень",
    "{name} пускает бумажный кораблик по морю-ванне",
    "{name} и зевающий медведь, что не может уснуть на зиму",
    "{name} находит ключ, открывающий любой сон",
    "{name} и вежливый монстрик под лестницей",
    "{name} и снеговик, мечтавший увидеть море",
    "{name} и лес поющих грибов",
    "{name} учит ворчливое облако улыбаться",
    "{name} и крошечный рыцарь, охраняющий чайник",
    "{name} и звезда, упавшая в суп",
    "{name} и волшебный карандаш, рисующий настоящее",
    "{name} устраивает проводы последней снежинке",
  ],
  en: [
    "{name} finds a talking dragon in the garden",
    "{name} saves the New Year",
    "How {name} made the moon and sun friends",
    "{name} and the lost star",
    "{name}’s big underwater adventure",
    "{name} and the rainbow that ran away",
    "{name} builds a rocket out of cardboard",
    "{name} meets a cloud that loves to sneeze",
    "{name} and the ticklish giant",
    "{name} opens a shop for lonely toys",
    "{name} and the umbrella that flies",
    "How {name} taught a snail to race",
    "{name} and the door in the old oak tree",
    "{name} bakes a cake for the whole forest",
    "{name} and the shy little ghost",
    "{name} rescues a mermaid from a puddle",
    "{name} and the sock-stealing goblin",
    "{name} paints the grey city with colors",
    "{name} and the library book that whispers",
    "{name} throws a party for the raindrops",
    "{name} and the mountain that hums lullabies",
    "{name} befriends a robot who forgot how to laugh",
    "{name} and the tiny circus under the bed",
    "{name} follows a map made of stars",
    "{name} and the winter that wouldn’t leave",
    "{name} teaches penguins to fly a kite",
    "{name} and the whispering seashell",
    "{name} and the honey thief in the meadow",
    "{name} discovers a city inside a music box",
    "{name} and the balloon that grants wishes",
    "{name} helps the autumn leaves find their way home",
    "{name} and the puppy made of clouds",
    "{name} and the secret door in the fridge",
    "{name} throws a birthday for a hundred-year-old turtle",
    "{name} and the sleepy volcano",
    "{name} and the letter from the future",
    "{name} and the garden that grows at night",
    "{name} befriends the last firefly of summer",
    "{name} and the runaway shadow",
    "{name} sails a paper boat across the bathtub sea",
    "{name} and the yawning bear who can’t hibernate",
    "{name} finds a key that opens any dream",
    "{name} and the polite little monster under the stairs",
    "{name} and the snowman who wanted to see the beach",
    "{name} and the forest of singing mushrooms",
    "{name} teaches a grumpy cloud to smile",
    "{name} and the tiny knight who guards the teapot",
    "{name} and the star that fell into the soup",
    "{name} and the magic pencil that draws real things",
    "{name} throws a farewell party for the last snowflake",
  ],
  cs: [
    "{name} najde v zahradě mluvícího dráčka",
    "{name} zachrání Vánoce",
    "Jak {name} spřátelil(a) měsíc a slunce",
    "{name} a ztracená hvězda",
    "Velké dobrodružství {name} pod vodou",
    "{name} a duha, která utekla",
    "{name} staví raketu z kartonu",
    "{name} potká obláček, který rád kýchá",
    "{name} a lechtivý obr",
    "{name} otevře obchod pro osamělé hračky",
    "{name} a deštník, který létá",
    "Jak {name} naučil(a) šneka závodit",
    "{name} a dvířka ve starém dubu",
    "{name} upeče dort pro celý les",
    "{name} a stydlivý duch",
    "{name} zachrání mořskou vílu z louže",
    "{name} a skřítek kradoucí ponožky",
    "{name} vybarví šedé město",
    "{name} a knížka z knihovny, která šeptá",
    "{name} uspořádá oslavu pro kapky deště",
    "{name} a hora, co si prozpěvuje ukolébavky",
    "{name} se spřátelí s robotem, který zapomněl se smát",
    "{name} a maličký cirkus pod postelí",
    "{name} jde podle mapy z hvězd",
    "{name} a zima, která nechtěla odejít",
    "{name} učí tučňáky pouštět draka",
    "{name} a šeptající mušle",
    "{name} a zloděj medu na louce",
    "{name} objeví město uvnitř hrací skříňky",
    "{name} a balónek plnící přání",
    "{name} pomáhá podzimnímu listí najít cestu domů",
    "{name} a štěně z obláčků",
    "{name} a tajná dvířka v lednici",
    "{name} uspořádá narozeniny stoleté želvě",
    "{name} a ospalá sopka",
    "{name} a dopis z budoucnosti",
    "{name} a zahrada, která roste v noci",
    "{name} se spřátelí s poslední světluškou léta",
    "{name} a uprchlý stín",
    "{name} pouští papírovou loďku po vaně-moři",
    "{name} a zívající medvěd, který nemůže usnout na zimu",
    "{name} najde klíč, který otevře každý sen",
    "{name} a zdvořilá příšerka pod schody",
    "{name} a sněhulák, který chtěl vidět moře",
    "{name} a les zpívajících hub",
    "{name} učí mrzutý mrak se usmívat",
    "{name} a maličký rytíř hlídající konvici",
    "{name} a hvězda, co spadla do polévky",
    "{name} a kouzelná tužka, která kreslí skutečné věci",
    "{name} uspořádá rozloučení s poslední vločkou",
  ],
  uk: [
    "{name} знаходить у саду балакучого дракончика",
    "{name} рятує Новий рік",
    "Як {name} потоваришував(ла) місяць і сонце",
    "{name} і загублена зірка",
    "Велика пригода {name} під водою",
    "{name} і веселка, що втекла",
    "{name} будує ракету з картону",
    "{name} зустрічає хмаринку, що любить чхати",
    "{name} і лоскітливий велетень",
    "{name} відкриває крамницю для самотніх іграшок",
    "{name} і парасолька, що літає",
    "Як {name} навчив(ла) равлика бігати наввипередки",
    "{name} і дверцята у старому дубі",
    "{name} пече пиріг для всього лісу",
    "{name} і сором’язливий привид",
    "{name} рятує русалку з калюжі",
    "{name} і гоблін, що краде шкарпетки",
    "{name} розмальовує сіре місто",
    "{name} і бібліотечна книжка, що шепоче",
    "{name} влаштовує свято для крапельок дощу",
    "{name} і гора, що наспівує колискові",
    "{name} товаришує з роботом, який розучився сміятися",
    "{name} і крихітний цирк під ліжком",
    "{name} йде за картою із зірок",
    "{name} і зима, що не хотіла йти",
    "{name} вчить пінгвінів запускати повітряного змія",
    "{name} і мушля, що шепоче",
    "{name} і викрадач меду на луці",
    "{name} знаходить місто всередині музичної скриньки",
    "{name} і кулька, що виконує бажання",
    "{name} допомагає осінньому листю знайти дорогу додому",
    "{name} і цуценя з хмаринок",
    "{name} і таємні дверцята в холодильнику",
    "{name} влаштовує день народження столітній черепасі",
    "{name} і сонний вулкан",
    "{name} і лист із майбутнього",
    "{name} і сад, що росте вночі",
    "{name} товаришує з останнім світлячком літа",
    "{name} і втекла тінь",
    "{name} пускає паперовий човник морем-ванною",
    "{name} і позіхаючий ведмідь, що не може заснути на зиму",
    "{name} знаходить ключ, що відмикає будь-який сон",
    "{name} і чемне мале чудовисько під сходами",
    "{name} і сніговик, що мріяв побачити море",
    "{name} і ліс співочих грибів",
    "{name} вчить буркотливу хмару усміхатися",
    "{name} і крихітний лицар, що охороняє чайник",
    "{name} і зірка, що впала в суп",
    "{name} і чарівний олівець, що малює справжнє",
    "{name} влаштовує проводи останній сніжинці",
  ],
  de: [
    "{name} findet im Garten einen sprechenden Drachen",
    "{name} rettet Weihnachten",
    "Wie {name} Mond und Sonne anfreundete",
    "{name} und der verlorene Stern",
    "{name}s großes Abenteuer unter Wasser",
    "{name} und der Regenbogen, der weglief",
    "{name} baut eine Rakete aus Pappe",
    "{name} trifft eine Wolke, die gern niest",
    "{name} und der kitzlige Riese",
    "{name} eröffnet einen Laden für einsame Spielsachen",
    "{name} und der Regenschirm, der fliegt",
    "Wie {name} einer Schnecke das Wettrennen beibrachte",
    "{name} und die Tür in der alten Eiche",
    "{name} backt einen Kuchen für den ganzen Wald",
    "{name} und das schüchterne Gespenst",
    "{name} rettet eine Meerjungfrau aus einer Pfütze",
    "{name} und der sockenklauende Kobold",
    "{name} malt die graue Stadt bunt",
    "{name} und das Bibliotheksbuch, das flüstert",
    "{name} feiert ein Fest für die Regentropfen",
    "{name} und der Berg, der Schlaflieder summt",
    "{name} freundet sich mit einem Roboter an, der das Lachen verlernt hat",
    "{name} und der winzige Zirkus unterm Bett",
    "{name} folgt einer Karte aus Sternen",
    "{name} und der Winter, der nicht gehen wollte",
    "{name} bringt Pinguinen das Drachensteigen bei",
    "{name} und die flüsternde Muschel",
    "{name} und der Honigdieb auf der Wiese",
    "{name} entdeckt eine Stadt in einer Spieluhr",
    "{name} und der Ballon, der Wünsche erfüllt",
    "{name} hilft den Herbstblättern nach Hause",
    "{name} und der Welpe aus Wolken",
    "{name} und die geheime Tür im Kühlschrank",
    "{name} feiert Geburtstag für eine hundertjährige Schildkröte",
    "{name} und der schläfrige Vulkan",
    "{name} und der Brief aus der Zukunft",
    "{name} und der Garten, der nachts wächst",
    "{name} freundet sich mit dem letzten Glühwürmchen des Sommers an",
    "{name} und der entlaufene Schatten",
    "{name} lässt ein Papierboot über das Badewannenmeer segeln",
    "{name} und der gähnende Bär, der nicht einschlafen kann",
    "{name} findet einen Schlüssel, der jeden Traum öffnet",
    "{name} und das höfliche Monster unter der Treppe",
    "{name} und der Schneemann, der das Meer sehen wollte",
    "{name} und der Wald der singenden Pilze",
    "{name} bringt einer mürrischen Wolke das Lächeln bei",
    "{name} und der winzige Ritter, der die Teekanne bewacht",
    "{name} und der Stern, der in die Suppe fiel",
    "{name} und der Zauberstift, der echte Dinge malt",
    "{name} verabschiedet die letzte Schneeflocke",
  ],
  fr: [
    "{name} trouve un petit dragon parlant dans le jardin",
    "{name} sauve le Nouvel An",
    "Comment {name} a lié d’amitié la lune et le soleil",
    "{name} et l’étoile perdue",
    "La grande aventure sous-marine de {name}",
    "{name} et l’arc-en-ciel qui s’est enfui",
    "{name} construit une fusée en carton",
    "{name} rencontre un nuage qui adore éternuer",
    "{name} et le géant chatouilleux",
    "{name} ouvre une boutique pour les jouets solitaires",
    "{name} et le parapluie qui vole",
    "Comment {name} a appris à un escargot à faire la course",
    "{name} et la porte dans le vieux chêne",
    "{name} prépare un gâteau pour toute la forêt",
    "{name} et le petit fantôme timide",
    "{name} sauve une sirène d’une flaque",
    "{name} et le lutin voleur de chaussettes",
    "{name} colorie la ville grise",
    "{name} et le livre de bibliothèque qui chuchote",
    "{name} organise une fête pour les gouttes de pluie",
    "{name} et la montagne qui fredonne des berceuses",
    "{name} se lie d’amitié avec un robot qui a oublié comment rire",
    "{name} et le minuscule cirque sous le lit",
    "{name} suit une carte faite d’étoiles",
    "{name} et l’hiver qui ne voulait pas partir",
    "{name} apprend aux pingouins à faire voler un cerf-volant",
    "{name} et le coquillage qui murmure",
    "{name} et le voleur de miel dans la prairie",
    "{name} découvre une ville dans une boîte à musique",
    "{name} et le ballon qui exauce les vœux",
    "{name} aide les feuilles d’automne à rentrer chez elles",
    "{name} et le chiot fait de nuages",
    "{name} et la porte secrète dans le frigo",
    "{name} organise un anniversaire pour une tortue centenaire",
    "{name} et le volcan endormi",
    "{name} et la lettre venue du futur",
    "{name} et le jardin qui pousse la nuit",
    "{name} se lie d’amitié avec la dernière luciole de l’été",
    "{name} et l’ombre en fuite",
    "{name} fait voguer un bateau en papier sur la mer-baignoire",
    "{name} et l’ours qui bâille et n’arrive pas à hiberner",
    "{name} trouve une clé qui ouvre tous les rêves",
    "{name} et le petit monstre poli sous l’escalier",
    "{name} et le bonhomme de neige qui voulait voir la mer",
    "{name} et la forêt des champignons chanteurs",
    "{name} apprend à un nuage grognon à sourire",
    "{name} et le petit chevalier qui garde la théière",
    "{name} et l’étoile tombée dans la soupe",
    "{name} et le crayon magique qui dessine de vraies choses",
    "{name} fait ses adieux au dernier flocon",
  ],
};
